import { istWochenende, letzteTage, tagesschluessel } from './zeit'

/**
 * Rechnerei hinter „Mein Tag" ([Z-02](../../../docs/phasen/zusatz-02-mein-tag.md)).
 *
 * ## Warum das hier steht und nicht in der Ansicht
 *
 * Jede Funktion in dieser Datei beantwortet eine Frage, die man **falsch** beantworten kann,
 * ohne dass es auffällt: „bewegt sich das Vorhaben", „wie lang ist meine Serie", „was klopft
 * an". Eine falsche Zahl in einer Ansicht sieht aus wie eine richtige – und wenn sie im JSX
 * steht, gibt es keine Stelle, an der man sie prüfen könnte.
 *
 * Alles hier ist rein: rein `Date` und Daten hinein, Zahlen und Listen heraus. Kein `new
 * Date()` ohne Übergabe, damit die Tests einen Stichtag setzen können.
 */

/* ─────────────────────────── Bewegung eines Vorhabens ─────────────────────── */

/**
 * **„bewegt sich" oder „steht"** – die ehrlichste Kennzahl für ein Vorhaben, und die einzige,
 * die sanften Zug erzeugt, ohne eine Alarmfarbe zu brauchen.
 *
 * Gerechnet aus dem jüngsten Ereignis über **alle** Aufgaben des Vorhabens, nicht nur die
 * eigenen: ein Vorhaben bewegt sich auch, wenn ein Kollege daran arbeitet.
 *
 * Die Schwellen sind bewusst grob. Zwischen „gestern" und „vorgestern" liegt keine Erkenntnis;
 * zwischen „gestern" und „vor sechs Tagen" schon.
 *
 * Woraus sich „Bewegung" rechnen *soll*, ist [F-62](../../../docs/OFFENE-FRAGEN.md) – der
 * hier benutzte Ereignisstrom ist der Vorschlag, nicht die endgültige Antwort.
 */
export const STEHT_AB_TAGEN = 5

export type Bewegungszustand = 'bewegt' | 'ruhig' | 'steht' | 'unbekannt'

export function bewegung(
  aufgaben: readonly { letztesEreignisAm?: string | Date | null }[],
  jetzt: Date,
): { zuletzt: Date | null; tage: number | null; zustand: Bewegungszustand } {
  let zuletzt: Date | null = null
  for (const a of aufgaben) {
    if (!a.letztesEreignisAm) continue
    const d = new Date(a.letztesEreignisAm)
    if (Number.isNaN(d.getTime())) continue
    if (!zuletzt || d > zuletzt) zuletzt = d
  }
  if (!zuletzt) return { zuletzt: null, tage: null, zustand: 'unbekannt' }

  /**
   * Kalendertage **zwischen den Datumsgrenzen**, nicht 24-Stunden-Blöcke: „gestern 23:50" und
   * „heute 00:10" sind zwei Tage, keine 20 Minuten. Wer in Stunden rechnet, bekommt am
   * frühen Morgen ein „bewegt sich", das gestern Abend noch „steht" hieß.
   */
  const tage = tageZwischen(tagesschluessel(zuletzt), tagesschluessel(jetzt))
  const zustand: Bewegungszustand =
    tage <= 1 ? 'bewegt' : tage >= STEHT_AB_TAGEN ? 'steht' : 'ruhig'
  return { zuletzt, tage, zustand }
}

/** Ganze Kalendertage zwischen zwei Tagesschlüsseln (`YYYY-MM-DD`). */
export function tageZwischen(von: string, bis: string): number {
  const a = Date.UTC(Number(von.slice(0, 4)), Number(von.slice(5, 7)) - 1, Number(von.slice(8, 10)))
  const b = Date.UTC(Number(bis.slice(0, 4)), Number(bis.slice(5, 7)) - 1, Number(bis.slice(8, 10)))
  return Math.round((b - a) / 86_400_000)
}

/* ─────────────────────────── Geschafft, Tagesziel, Serie ──────────────────── */

export type ErledigterTag = {
  tag: string
  anzahl: number
  /** Innerhalb eines Tages **absteigend**: das zuletzt Fertige steht vorn – siehe unten. */
  aufgaben: { id: string; title: string; am: string }[]
}

/**
 * **Was an welchem Tag fertig wurde.**
 *
 * Maßgeblich ist `updatedAt` und nicht das Ereignis-Log: die Zone zeigt *„was habe ich
 * geschafft"*, und dafür ist der Zeitpunkt gemeint, an dem die Aufgabe auf „Erledigt" stand.
 * Ein späterer Kommentar verschiebt das nicht.
 *
 * Zurückgegeben wird **jeder** Tag des Zeitraums, auch die leeren – die Serie und der
 * Verlaufsstreifen brauchen die Lücken, sonst zählen sie über sie hinweg.
 *
 * ## Warum die Aufgaben eines Tages sortiert sind (28.08.2026, [Z-12](../../../docs/phasen/zusatz-12-mein-tag-einfach.md))
 *
 * Vorher standen sie in der Reihenfolge, in der `tasks.all` sie lieferte – also in keiner. Das
 * fiel nicht auf, solange „Geschafft" drei Tage nebeneinander als Blöcke zeigte: dort ist der
 * Tag die Einheit, nicht die Aufgabe.
 *
 * Der Modus „Einfach" macht daraus eine **senkrechte Zeitachse**: „Jetzt" in der Mitte, darunter
 * das Erledigte mit dem Neuesten oben. Diese Zusage kann die Zone nur halten, wenn die
 * Reihenfolge hier entsteht – sie im Client nachzusortieren hieße, den Zeitpunkt zweimal zu
 * führen. Deshalb trägt jeder Eintrag jetzt sein `am` und die Liste eines Tages fällt.
 */
export function erledigteJeTag(
  aufgaben: readonly { id: string; title: string; status: string; updatedAt: string | Date }[],
  tage: readonly string[],
): ErledigterTag[] {
  const karte = new Map<string, { id: string; title: string; am: string }[]>()
  for (const tag of tage) karte.set(tag, [])

  for (const a of aufgaben) {
    if (a.status !== 'erledigt') continue
    const am = new Date(a.updatedAt)
    const eintrag = karte.get(tagesschluessel(am))
    if (eintrag) eintrag.push({ id: a.id, title: a.title, am: am.toISOString() })
  }

  return tage.map((tag) => {
    const aufg = (karte.get(tag) ?? []).sort((x, y) => y.am.localeCompare(x.am))
    return { tag, anzahl: aufg.length, aufgaben: aufg }
  })
}

/** Untere und obere Grenze des vorgeschlagenen Tagesziels. */
export const TAGESZIEL_MIN = 1
export const TAGESZIEL_MAX = 8
/** Der Zeitraum, aus dem der Vorschlag kommt – vier Wochen. */
export const TAGESZIEL_FENSTER_TAGE = 28

/**
 * **Wie viele Aufgaben sind ein guter Tag?** – aus dem eigenen Schnitt der letzten vier Wochen.
 *
 * Gerechnet wird über **Werktage**, nicht über Kalendertage: wer freitags fünf schafft und am
 * Wochenende nichts, hat keinen Schnitt von 1,4. Das Wochenende ist keine schlechte Leistung,
 * es ist keine Leistung.
 *
 * Aufgerundet, nicht abgerundet – ein Ziel, das man im Schnitt trifft, ist keines. Aber
 * gedeckelt: acht Häkchen an einem Tag sind kein Ziel, sondern eine Drohung.
 *
 * Ob das Ziel überhaupt aus dem Schnitt kommen soll, ist [F-63](../../../docs/OFFENE-FRAGEN.md).
 */
export function tagesziel(verlauf: readonly ErledigterTag[]): number {
  const werktage = verlauf.filter((t) => !istWochenende(t.tag))
  if (werktage.length === 0) return TAGESZIEL_MIN
  const summe = werktage.reduce((s, t) => s + t.anzahl, 0)
  const schnitt = summe / werktage.length
  return Math.min(TAGESZIEL_MAX, Math.max(TAGESZIEL_MIN, Math.ceil(schnitt)))
}

/**
 * **Die Serie, mit Anstand.**
 *
 * „12 Tage in Folge etwas fertig gemacht." Drei Regeln, und jede einzelne ist der Grund,
 * warum die Anzeige nicht ärgerlich wird:
 *
 * 1. **Freie Tage brechen die Serie nicht und verlängern sie nicht.** Wochenende, Urlaub,
 *    Krankheit – das System kennt die Abwesenheiten ohnehin ([Z-01](../../../docs/phasen/zusatz-01-abwesenheiten.md)).
 *    Eine Serie, die der Urlaub killt, ist eine Strafe fürs Urlaubnehmen.
 * 2. **Der heutige Tag zählt nur mit, wenn schon etwas fertig ist** – er beendet die Serie
 *    aber nicht. Um 9 Uhr hat noch niemand etwas geschafft; eine Null wäre keine Auskunft,
 *    sondern ein Vorwurf.
 * 3. **Rückwärts bis zur ersten Lücke an einem Arbeitstag.** Danach wird nicht weitergesucht.
 *
 * `frei` beantwortet „an diesem Tag wurde nicht gearbeitet" – Wochenende **oder** Abwesenheit.
 */
export function serie(
  verlauf: readonly ErledigterTag[],
  heute: string,
  frei: (tag: string) => boolean,
): number {
  const karte = new Map(verlauf.map((t) => [t.tag, t.anzahl]))
  /** Nur die Tage bis heute – ein Verlauf, der in die Zukunft reicht, zählt nicht mit. */
  const tage = verlauf
    .map((t) => t.tag)
    .filter((t) => t <= heute)
    .sort()
    .reverse()

  let laenge = 0
  for (const tag of tage) {
    if (frei(tag)) continue
    const anzahl = karte.get(tag) ?? 0
    if (anzahl > 0) {
      laenge++
      continue
    }
    // Regel 2: der laufende Tag beendet nichts, er hat nur noch nicht angefangen.
    if (tag === heute) continue
    break
  }
  return laenge
}

/* ─────────────────────────── Zone 0: Klopft an ────────────────────────────── */

export const KLOPF_ARTEN = [
  'sofort',
  'antwort',
  'erwaehnung',
  'frist',
  'abnahme',
  'vorwarnung',
] as const
export type Klopfart = (typeof KLOPF_ARTEN)[number]

export const KLOPF_WORT: Record<Klopfart, string> = {
  sofort: 'Sofort',
  antwort: 'Antwort',
  erwaehnung: 'Erwähnung',
  frist: 'Überfällig',
  abnahme: 'Du bist dran',
  vorwarnung: 'Bald fällig',
}

/**
 * **Welche Arten einen Gelesen-Haken tragen** ([E-229](../../../docs/ENTSCHEIDUNGEN.md#e-229)).
 *
 * Nur die beiden Nachrichten-Sorten. Eine Frist ist nicht erledigt, weil man sie zur Kenntnis
 * genommen hat, und eine Abnahme erst recht nicht. Die Liste steht hier und nicht in der
 * Oberfläche, damit eine sechste Art nicht stillschweigend ohne Haken bleibt.
 */
export const KLOPF_ABHAKBAR: readonly Klopfart[] = ['antwort', 'erwaehnung']

export type Klopfmeldung = {
  art: Klopfart
  taskId: string
  titel: string
  /** Die Nebenzeile: warum diese Zeile hier steht, in Worten der Lage – nicht der Technik. */
  hinweis: string
  /** Seit wann. Sortiert innerhalb einer Art; `null`, wenn nicht bekannt. */
  seit: Date | null
  /**
   * **Abgehakt, aber noch im Nachlauf** – nur bei den Arten aus {@link KLOPF_ABHAKBAR} je `true`.
   *
   * Die übrigen Sorten haben keinen Gelesen-Stand: eine Frist ist nicht abgelaufen, weil
   * man sie zur Kenntnis genommen hat. Nur der Kommentar ist eine Nachricht, und Nachrichten
   * liest man. Wer hier `true` sieht, zeigt die Zeile blass mit gesetztem Haken – siehe
   * `NACHLAUF_MS` in `web/src/lib/gelesen.ts` und
   * [E-229](../../../docs/ENTSCHEIDUNGEN.md#e-229).
   */
  gelesen: boolean
  /**
   * **Lässt sich von hier aus starten** (09.09.2026, [E-337](../../../docs/ENTSCHEIDUNGEN.md#e-337)) –
   * nur bei einer abgelaufenen Frist an einer Aufgabe, die mir gehört und noch nicht läuft.
   * Christian Hunhoff, mit Bild der Zeile „Überfällig · Ansehen": *„Eventuell hier auch ein
   * «Starten»-Button?"* Dieselbe Handlung wie in „Was als Nächstes" – was anklopft, soll laufen.
   */
  startbar?: boolean
}

/**
 * Wie viele Zeilen die Zone höchstens zeigt. Der Rest wird **gezählt**, nicht ausgebreitet –
 * sonst wird aus der Zone eine zweite Aufgabenliste und aus dem Rot eine Wand.
 */
export const KLOPF_MAX_ZEILEN = 3

/**
 * **Was heute nicht bis morgen warten kann.**
 *
 * Die Zone ist das Gegenstück zu „Ruhig gestellt", und ihre wichtigste Eigenschaft ist, dass
 * sie **meistens leer ist**. Deshalb ist die Aufnahmeregel eng: nicht „wichtig" kommt hinein,
 * sondern nur eines von vier Dingen.
 *
 * | Art | Bedingung |
 * |---|---|
 * | `sofort` | liegt im Sofort-Vorrat und **niemand** hat sie gezogen |
 * | `erwaehnung` | ungelesener Kommentar, in dem mich jemand **beim Namen genannt** hat |
 * | `antwort` | ungelesener Kommentar auf einer Aufgabe, die bei **mir gerade läuft** |
 * | `frist` | **meine** Aufgabe, deren **wirksamer Termin** vorbei ist (E-281) |
 * | `abnahme` | wartet auf **meine** Abnahme – ich blockiere jemand anderen |
 * | `vorwarnung` | ein Termin, an den ich mich **selbst erinnern lassen wollte** (Z-13) |
 *
 * Reihenfolge: erst `sofort` (jemand hat eine Störung gemeldet und niemand ist dran), dann
 * `abnahme` (ein Mensch wartet auf mich), dann `erwaehnung`, dann `frist`, dann `vorwarnung`,
 * dann `antwort`. Innerhalb einer Art das Älteste zuerst.
 *
 * **Warum die Vorwarnung über der Antwort steht und damit die „Mensch schlägt Uhr"-Regel
 * bricht** (28.08.2026, Z-13): sie ist nicht die Uhr des Systems, sondern eine **Bitte des
 * Benutzers** – jemand hat an genau dieser Aufgabe „Benachrichtigen" angehakt. Eine
 * ausdrücklich bestellte Erinnerung, die hinter „+ 3 weitere" verschwindet, ist keine
 * Erinnerung; sie wäre die erste Funktion, von der jemand sagt, sie funktioniere nicht. Unter
 * `frist` steht sie trotzdem: was schon abgelaufen ist, wiegt schwerer als was noch kommt.
 *
 * **Warum die Erwähnung über der Frist steht** (28.08.2026, Z-11): eine abgelaufene Frist hält
 * niemanden auf außer mich selbst und sie steht morgen noch genauso da. Eine direkte Ansprache
 * hält einen anderen Menschen auf und veraltet – wer zwei Tage lang keine Antwort bekommt,
 * fragt nicht noch einmal, sondern macht es anders. Deshalb dieselbe Nachbarschaft wie
 * `abnahme`: alles, wo ein Mensch wartet, steht über allem, wo eine Uhr läuft.
 *
 * **Ohne Dopplung:** dieselbe Aufgabe erscheint höchstens einmal, mit ihrer dringlichsten Art.
 *
 * **Abgehakte Antworten bleiben kurz stehen** ([E-229](../../../docs/ENTSCHEIDUNGEN.md#e-229)).
 * Sie kommen mit gesetztem `gelesen` herein und behalten ihren Platz in der Reihenfolge – die
 * Zeile darf unter dem Zeiger nicht wegspringen, sonst trifft der nächste Klick die falsche
 * Aufgabe. Wie lange „kurz" ist, entscheidet der Aufrufer, indem er sie irgendwann nicht mehr
 * mitgibt; hier gibt es dafür keine Uhr.
 */
export function klopftAn(lage: {
  ichId: string
  /**
   * Der heutige Tagesschlüssel. Die Wiedervorlage wartender Aufgaben las ihn bis zum 16.09.2026;
   * seitdem steht sie im Vorlauf (E-357). Bleibt, damit die nächste Tagesregel nicht erst die
   * Aufrufer umbauen muss.
   */
  heute: string
  /** Ungezogene Aufgaben aus Pools, die das WIP-Limit durchbrechen dürfen (= „Sofort"). */
  sofortImVorrat: readonly { id: string; title: string; addedAt?: Date | string | null }[]
  /**
   * Meine Aufgaben – für Fristen, Abnahmen und den Abgleich mit dem Feed.
   *
   * **`fristAbgelaufen` kommt aus dem wirksamen Termin, nicht aus dem Zeitfenster**
   * (31.08.2026, [E-281](../../../docs/ENTSCHEIDUNGEN.md#e-281)). Vorher hießen die beiden
   * Felder `zeitfensterAbgelaufen`/`zeitfensterDatum` und wurden auch so gefüllt – mit der
   * Folge, dass eine gerissene **Fälligkeit** hier nie ankam: sie steht in `due_at` und hat
   * mit dem Zeitfenster nichts zu tun. Ein Datum, das ein Mensch ausdrücklich gesetzt hat, war
   * damit die einzige Terminart ohne Wirkung.
   */
  meine: readonly {
    id: string
    title: string
    status: string
    /** Ist der wirksame Termin vorbei? Fälligkeit → Meilenstein → Zieltermin → Zeitfenster. */
    fristAbgelaufen: boolean
    /** Wie lange schon – „3 Tage überfällig". `null`, wenn es keinen Termin gibt. */
    fristText: string | null
  }[]
  /** Alles, was auf meine Abnahme wartet (`abnahmeDurchId === ich`, Status `review`). */
  meineAbnahmen: readonly {
    id: string
    title: string
    bearbeiter: string | null
    seit: Date | null
  }[]
  /**
   * Ungelesene Kommentare, schon auf meine laufenden Aufgaben eingegrenzt – **und die eben
   * abgehakten**, solange ihr Nachlauf läuft. Wer den Haken setzt, soll ihn zurücknehmen
   * können; das geht nur, wenn die Zeile noch da ist.
   */
  antworten: readonly {
    taskId: string
    titel: string
    wer: string | null
    am: Date
    /** Wann abgehakt – `null` heißt ungelesen. Die Vorauswahl trifft der Aufrufer. */
    gelesenAm: Date | null
    /**
     * **Hat mich jemand beim Namen genannt?** Dann wird die Zeile zur `erwaehnung` und steht
     * weiter oben. Ohne dieses Feld hieße jede Ansprache „Antwort" – und genau das war der
     * Befund vom 28.08.2026: Erwähnungen stiegen zwar auf, aber unter falschem Namen und
     * ununterscheidbar von einem Kommentar an einer Aufgabe, die ohnehin bei mir läuft.
     */
    erwaehntMich?: boolean
  }[]
  /**
   * **Termine, an die ich mich erinnern lassen wollte** ([Z-13](../../../docs/phasen/zusatz-13-frist-vorwarnung.md)).
   *
   * Schon gefiltert: der Aufrufer gibt nur herein, was im **eigenen** Vorwarnfenster liegt
   * (`imVorwarnfenster`) und wofür es ein Abo gibt. Diese Funktion entscheidet nicht, *wen*
   * ein Termin angeht – das ist eine Frage an die Datenbank, nicht an eine reine Rechnung.
   *
   * Anders als bei `frist` steht die Aufgabe hier **nicht** notwendig bei mir: der ganze Sinn
   * des Abos ist, einen Termin im Blick zu behalten, den ein anderer bearbeitet.
   */
  vorwarnungen?: readonly { taskId: string; titel: string; termin: Date; hinweis: string }[]
}): Klopfmeldung[] {
  const meldungen: Klopfmeldung[] = []
  const gesehen = new Set<string>()

  const dazu = (m: Klopfmeldung) => {
    if (gesehen.has(m.taskId)) return
    gesehen.add(m.taskId)
    meldungen.push(m)
  }

  for (const a of [...lage.sofortImVorrat].sort(nachAelter((x) => zuDatum(x.addedAt)))) {
    dazu({
      art: 'sofort',
      taskId: a.id,
      titel: a.title,
      hinweis: 'im Sofort-Vorrat, niemand ist dran',
      seit: zuDatum(a.addedAt),
      gelesen: false,
    })
  }

  for (const a of [...lage.meineAbnahmen].sort(nachAelter((x) => x.seit))) {
    dazu({
      art: 'abnahme',
      taskId: a.id,
      titel: a.title,
      hinweis: a.bearbeiter
        ? `${a.bearbeiter} wartet auf deine Abnahme`
        : 'wartet auf deine Abnahme',
      seit: a.seit,
      gelesen: false,
    })
  }

  /**
   * **Die Nachrichten: die neueste zuerst** (Nils, 24.08.2026: *„da steht der neuste Kommentar
   * unten und nicht oben. Da nicht alle angezeigt werden, sieht man nur die ältesten."*).
   *
   * Die drei anderen Sorten stehen **ältestes zuerst**, und das bleibt so: dort ist die
   * Wartezeit das Dringlichkeitsmaß – eine Störung, die seit zwei Stunden niemand hat, ist
   * schlimmer als eine von eben, und wer am längsten auf eine Abnahme wartet, wird am längsten
   * aufgehalten.
   *
   * Bei einem Kommentar ist es umgekehrt. Er ist kein Rückstand, sondern ein Gespräch: die
   * jüngste Äußerung ist die, auf die man antwortet. Zusammen mit der Deckelung auf
   * {@link KLOPF_MAX_ZEILEN} Zeilen war die alte Reihenfolge sogar aktiv schädlich – sichtbar
   * waren ausgerechnet die drei ältesten, und das Neue verschwand hinter „+ 6 weitere".
   *
   * **Einmal sortiert, zweimal durchlaufen** – erst die Erwähnungen, nach der Frist der Rest.
   * Dieselbe Liste zweimal zu sortieren wäre dieselbe Reihenfolge zum zweiten Mal.
   */
  const nachrichten = [...lage.antworten].sort(nachNeuer((x) => x.am))

  for (const k of nachrichten) {
    if (!k.erwaehntMich) continue
    dazu({
      art: 'erwaehnung',
      taskId: k.taskId,
      titel: k.titel,
      hinweis: k.wer ? `${k.wer} hat dich erwähnt` : 'du wurdest erwähnt',
      seit: k.am,
      gelesen: !!k.gelesenAm,
    })
  }

  for (const a of lage.meine) {
    if (a.status === 'erledigt' || !a.fristAbgelaufen) continue
    dazu({
      art: 'frist',
      taskId: a.id,
      titel: a.title,
      hinweis: a.fristText ?? 'Frist ist abgelaufen',
      seit: null,
      gelesen: false,
      startbar: a.status === 'bereit' || a.status === 'eingang',
    })
  }

  /**
   * **Die bestellten Vorwarnungen** – das Nächstliegende zuerst.
   *
   * Nicht „ältestes zuerst" wie bei den drei Sorten darüber: dort ist die Wartezeit das
   * Dringlichkeitsmaß. Hier ist es der **Abstand zum Termin** – was übermorgen fällig ist,
   * drängt mehr als was in zwei Wochen fällig ist, ganz gleich, wann jemand das Häkchen
   * gesetzt hat.
   */
  for (const v of [...(lage.vorwarnungen ?? [])].sort(
    (a, b) => a.termin.getTime() - b.termin.getTime(),
  )) {
    dazu({
      art: 'vorwarnung',
      taskId: v.taskId,
      titel: v.titel,
      hinweis: v.hinweis,
      seit: null,
      gelesen: false,
    })
  }

  /** Der zweite Durchlauf über `nachrichten` – was keine Erwähnung war, ist eine Antwort. */
  for (const k of nachrichten) {
    dazu({
      art: 'antwort',
      taskId: k.taskId,
      titel: k.titel,
      hinweis: k.wer ? `${k.wer} hat geantwortet` : 'neuer Kommentar',
      seit: k.am,
      gelesen: !!k.gelesenAm,
    })
  }

  return meldungen
}

function zuDatum(wert: Date | string | null | undefined): Date | null {
  if (!wert) return null
  const d = new Date(wert)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Ältestes zuerst; was kein Datum hat, kommt ans Ende. */
/** Neuestes zuerst; was kein Datum hat, kommt ans Ende. Für Kommentare – siehe `klopftAn`. */
function nachNeuer<T>(feld: (t: T) => Date | null): (a: T, b: T) => number {
  const aelter = nachAelter(feld)
  return (a, b) => {
    const x = feld(a)
    const y = feld(b)
    /* Ohne Datum bleibt hinten – auch beim Umdrehen, sonst wanderte es nach vorn. */
    if (!x || !y) return aelter(a, b)
    return -aelter(a, b)
  }
}

function nachAelter<T>(feld: (t: T) => Date | null): (a: T, b: T) => number {
  return (a, b) => {
    const x = feld(a)
    const y = feld(b)
    if (!x && !y) return 0
    if (!x) return 1
    if (!y) return -1
    return x.getTime() - y.getTime()
  }
}

/* ─────────────────────────── Hilfen für die Ansicht ───────────────────────── */

/** Die Tagesschlüssel, aus denen „Geschafft", Tagesziel und Serie rechnen. */
export function verlaufsTage(bis: Date, anzahl = TAGESZIEL_FENSTER_TAGE): string[] {
  return letzteTage(anzahl, bis)
}

/* ── „Das sieht gerade niemand an" ─────────────────────────────────────── */

/** Ab wann eine unberührte Aufgabe als übersehen gilt. */
export const UEBERSEHEN_TAGE = 14

/**
 * **Die Aufgabe, auf die niemand mehr schaut**
 * ([Z-19](../../../docs/phasen/zusatz-19-tagesplan.md) AP 19.10).
 *
 * Nils' Diagnose am 30.09.2026 lautete: *„Ich denke ich habe Angst das es im backlog untergeht
 * daher schon in Bearbeitung gemacht."* Die Angst ist begründet – es gibt in Work tatsächlich
 * einen Zustand, in dem eine Aufgabe von **keiner** Ansicht mehr aufgegriffen wird:
 *
 * - **ohne Zeitfenster** → der Vorlauf ordnet sie ganz nach hinten
 * - **ohne Frist** → nichts macht sie dringend
 * - **ohne Wiedervorlage** → nichts legt sie wieder vor ([E-357](../../../docs/ENTSCHEIDUNGEN.md#e-357))
 * - **seit zwei Wochen unberührt** → auch niemand sonst hat hingesehen
 *
 * Alle vier zusammen heißen: Diese Aufgabe existiert, aber sie wird niemandem mehr begegnen.
 * **Genau das ist der Zustand, den das „Lesezeichen in Arbeit" verhindern sollte.**
 *
 * ## Warum alle vier Bedingungen und nicht eine
 *
 * Jede für sich ist harmlos und häufig. Eine Aufgabe ohne Frist ist der Normalfall; am
 * 30.09.2026 hatten **0 von 141** offenen Aufgaben überhaupt ein Zeitfenster. Würde eine davon
 * allein die Meldung auslösen, leuchtete sie bei fast allem – und eine Warnung, die immer
 * leuchtet, ist keine.
 *
 * ## Warum 14 Tage
 *
 * Die acht liegengebliebenen „in Arbeit"-Aufgaben vom 30.09.2026 lagen seit **5 bis 12** Tagen.
 * Eine Grenze darunter hätte normales Arbeiten gemeldet; 14 Tage sind zwei volle Wochen, also
 * auch bei einer Woche Urlaub dazwischen eine echte Aussage.
 *
 * `jetzt` wird hereingegeben, damit die Funktion prüfbar bleibt.
 */
export function siehtNiemandAn(
  aufgabe: {
    zeitfenster?: string | null
    dueAt?: string | Date | null
    wartenBis?: string | null
    updatedAt?: string | Date | null
  },
  jetzt: Date = new Date(),
): boolean {
  if (aufgabe.zeitfenster) return false
  if (aufgabe.dueAt) return false
  if (aufgabe.wartenBis) return false
  if (!aufgabe.updatedAt) return false
  const beruehrt = new Date(aufgabe.updatedAt).getTime()
  if (!Number.isFinite(beruehrt)) return false
  return (jetzt.getTime() - beruehrt) / (24 * 60 * 60 * 1000) >= UEBERSEHEN_TAGE
}
